import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { 
  Box, Button, CircularProgress, Grid, IconButton, Paper, TableCell, TableContainer, 
  TableRow, Tooltip, Table, TableHead, TableBody 
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import SearchBox from '../Common/SearchBox';
import { getFacilityProjectsByClient, deleteFacilityProject, setStoreData } from '../../Redux/actions';
import ConfirmDialog from '../Common/ConfirmDialog';
import moment from 'moment';

export default function ProjectManagement() {
  const { client_id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [queryParams] = useSearchParams();
  const existingParams = Object.fromEntries([...queryParams]);
  
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  const tableColumns = ['Sl.No.', 'Description', 'Facility Name', 'Budget', 'Created Date', ''];

  useEffect(() => {
    fetchProjects();
  }, [client_id, queryParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProjects = () => {
    setLoading(true);
    const params = {
      search: existingParams?.search || '',
    };
    dispatch(getFacilityProjectsByClient(client_id))
      .then((res) => {
        if (res?.status === 200 && res.data?.success) {
          let data = res.data.data || [];
          if (params.search) {
            data = data.filter(proj => 
              proj.facility?.facility_name?.toLowerCase().includes(params.search.toLowerCase())
            );
          }
          setProjects(data);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const handleCreate = () => {
    navigate(`/client/${client_id}/settings/project_management/create`);
  };

  const handleEdit = (id) => {
    navigate(`/client/${client_id}/settings/project_management/edit/${id}`);
  };

  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    setShowConfirmDialog(true);
  };

  const handleDeleteConfirm = () => {
    setShowConfirmDialog(false);
    dispatch(deleteFacilityProject(selectedItem.id)).then((res) => {
      if (res?.status === 200 && res.data?.success) {
        dispatch(setStoreData('alertMessage', {
          msg: res.data?.message || 'Project deleted successfully',
          type: 'success',
          handleClose: () => dispatch(setStoreData('alertMessage', null)),
        }));
        fetchProjects();
      } else {
        dispatch(setStoreData('alertMessage', {
          msg: res.data?.message || 'Failed to delete project',
          type: 'fail',
          handleClose: () => dispatch(setStoreData('alertMessage', null)),
        }));
      }
    });
  };

  const handleSearch = (value) => {
    const params = new URLSearchParams(existingParams);
    if (value) {
      params.set('search', value);
    } else {
      params.delete('search');
    }
    navigate({ search: params.toString() }, { replace: true });
  };

  let tableContent = null;
  if (loading) {
    tableContent = (
      <TableRow>
        <TableCell colSpan={6} align="center">
          <CircularProgress sx={{ color: '#008080' }} />
        </TableCell>
      </TableRow>
    );
  } else if (!loading && projects.length === 0) {
    tableContent = (
      <TableRow>
        <TableCell colSpan={6} align="center">
          <h5 className="w3-text-grey">No Data Found</h5>
        </TableCell>
      </TableRow>
    );
  } else {
    tableContent = projects.map((item, index) => (
      <TableRow 
        key={item.id} 
        onMouseEnter={() => setHoveredRowId(item.id)}
        onMouseLeave={() => setHoveredRowId(null)}
      >
        <TableCell>{index + 1}</TableCell>
        <TableCell>{item.description || '-'}</TableCell>
        <TableCell>{item?.facility?.facility_name || '-'}</TableCell>
        <TableCell>
          {item.project_budget ? parseFloat(item.project_budget).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '-'}
        </TableCell>
        <TableCell>
          {item.created_at ? moment(item.created_at).format('DD/MM/YYYY') : '-'}
        </TableCell>
        <TableCell>
          <Box display="flex" justifyContent="right">
            <Tooltip title="Edit">
              <IconButton 
                size="small" 
                style={{ opacity: hoveredRowId === item.id ? 1 : 0, paddingLeft: '0px' }}
                onClick={() => handleEdit(item.id)}
              >
                <Edit style={{ color: 'black' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton 
                size="small" 
                style={{ opacity: hoveredRowId === item.id ? 1 : 0, paddingLeft: '0px' }}
                onClick={() => handleDeleteClick(item)}
              >
                <Delete style={{ color: '#d32f2f' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>
    ));
  }

  return (
    <div>
      <Grid container>
        <Grid item md={12}>
          <Grid item container spacing={8} paddingY={2}>
            <Grid item md={6}>
              <Button 
                style={{ backgroundColor: '#008080', color: 'white' }} 
                variant="contained" 
                onClick={handleCreate}
              >
                Create Project
              </Button>
            </Grid>
            <Grid item md={6} container justifyContent="flex-end" direction="row">
              <Grid item container md={12} direction="row">
                <Grid item md={12} container justifyContent="flex-end">
                  <h6 style={{ paddingRight: '5px', fontSize: '15px' }}>Search By: </h6>
                  <SearchBox 
                    search={handleSearch} 
                    placeholder="Facility" 
                    value={existingParams?.search || ''} 
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          
          <Grid paddingX={0} paddingY={1}>
            <TableContainer component={Paper}>
              <Table aria-label="simple table" style={{ tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow>
                    {tableColumns.map((col, index) => (
                      <TableCell 
                        key={index}
                        style={{ fontSize: '14px', fontWeight: 'bold' }}
                      >
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableContent}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Grid>

      {showConfirmDialog && (
        <ConfirmDialog
          handleCancel={() => setShowConfirmDialog(false)}
          handleOk={handleDeleteConfirm}
          message="Are you sure you want to delete this project?"
        />
      )}
    </div>
  );
}
